import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';

/**
 * MUI TextField wrapper for numeric input that fixes two paper-cuts of
 * `<TextField type="number">`:
 *
 * 1. Empty input state is preserved during editing. Replacing the previous
 *    pattern (`value={x || 0}` + `parseFloat(e.target.value) || 0`) which
 *    silently coerced a cleared field back to 0 — typing a new value
 *    afterwards then produced "05" instead of "5".
 * 2. Mouse-wheel scrolling no longer changes the value. The input is blurred
 *    on wheel, so the wheel keeps propagating to the page (page scrolls)
 *    while the focus is gone before the browser applies an increment.
 *
 * Callers receive `number | undefined` (undefined === empty) and decide
 * their own fallback. For required-numeric fields use `v ?? 0` at the call
 * site; for optional fields just pass through.
 */
export type NumberFieldProps = Omit<
    React.ComponentProps<typeof TextField>,
    'value' | 'onChange' | 'type' | 'onWheel'
> & {
    value: number | undefined | null;
    onValueChange: (value: number | undefined) => void;
    /** When true, parse with parseInt instead of parseFloat. */
    integer?: boolean;
};

export const NumberField: React.FC<NumberFieldProps> = ({
    value,
    onValueChange,
    integer,
    onFocus,
    onBlur,
    ...rest
}) => {
    const [text, setText] = useState<string>(value == null ? '' : String(value));
    const [hasFocus, setHasFocus] = useState(false);

    useEffect(() => {
        if (!hasFocus) {
            setText(value == null ? '' : String(value));
        }
    }, [value, hasFocus]);

    return (
        <TextField
            {...rest}
            type="number"
            value={text}
            onFocus={(e) => {
                setHasFocus(true);
                onFocus?.(e);
            }}
            onChange={(e) => {
                const v = e.target.value;
                setText(v);
                if (v === '') {
                    onValueChange(undefined);
                    return;
                }
                const n = integer ? parseInt(v, 10) : parseFloat(v);
                if (!Number.isNaN(n)) {
                    onValueChange(n);
                }
            }}
            onBlur={(e) => {
                setHasFocus(false);
                onBlur?.(e);
            }}
            onWheel={(e) => {
                (e.target as HTMLInputElement).blur();
            }}
        />
    );
};
